import classNames from "classnames";
import { useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { DashboardUtils } from "../dashboard_utils";
import { ModifySettingsButton } from "./improved_components/base_components/modify_settings_button";

function formatPluralDuration(num: number, label: "hour" | "minute") {
	return num === 1 ? `${num} ${label}` : `${num} ${label}s`;
}

interface DurationDropdownElement {
	label: JSX.Element | string;
	value: number;
}

export default function DurationComponent(props: {
	duration_ms: number;
	updateFunc: (value: number) => void;
}) {
	const minutes_in_ms = 1000 * 60;
	const hours_in_ms = minutes_in_ms * 60;

	const hours = Math.floor(props.duration_ms / hours_in_ms);
	const minutes = (props.duration_ms % hours_in_ms) / minutes_in_ms;

	return (
		<div className="flex w-full flex-col gap-2">
			<div className="flex items-center gap-2">
				<p className="font-bold">Duration</p>
				{/* <div
					className={classNames(
						"fas fa-times self-end opacity-20 hover:text-warning hover:opacity-100"
					)}
				></div> */}
			</div>
			<div className="ml-auto flex w-full items-center gap-2">
				{/* <i className="fas fa-stopwatch text-2xl"></i> */}
				<div className="w-1/2 sm:w-1/3">
					<DurationDropdown
						setValue={(val) =>
							props.updateFunc(
								val * hours_in_ms + minutes_in_ms * minutes
							)
						}
						value={hours}
						type="hour"
					/>
				</div>
				<div className="w-1/2 sm:w-1/3">
					<DurationDropdown
						setValue={(val) =>
							props.updateFunc(
								hours * hours_in_ms + val * minutes_in_ms
							)
						}
						value={minutes}
						type="minute"
					/>
				</div>
				<div
					className="w-fit cursor-pointer text-xl opacity-50 hover:text-warning hover:opacity-100"
					onClick={() => props.updateFunc(undefined)}
				>
					<div className="fas fa-times"></div>
				</div>
			</div>
		</div>
	);
}

function createInitialHourOptions(val: number) {
	const hour_options: { label: JSX.Element | string; value: number }[] = [];
	for (let i = 0; i < 24; i++) {
		hour_options.push({
			label: formatPluralDuration(i, "hour"),
			value: i,
		});
	}

	if (val >= 24) {
		hour_options.push({
			label: formatPluralDuration(val, "hour"),
			value: val,
		});
	}

	return hour_options;
}

function createInitialMinuteOptions(val: number) {
	const minute_options: { label: JSX.Element | string; value: number }[] = [];
	for (let i = 0; i < 60; i += 5) {
		if (val > i && val < i + 5) {
			minute_options.push({
				label: formatPluralDuration(val, "minute"),
				value: val,
			});
		}

		minute_options.push({
			label: formatPluralDuration(i, "minute"),
			value: i,
		});
	}

	return minute_options;
}

function DurationDropdown(props: {
	type: "hour" | "minute";
	value: number;
	setValue: (num: number) => void;
}) {
	const [options, setOptions] = useState(
		props.type === "hour"
			? createInitialHourOptions(props.value)
			: createInitialMinuteOptions(props.value)
	);

	const selected_option = options.find((val) => val.value === props.value);

	return (
		<div className="field w-full">
			<div className="control w-full">
				<CreatableSelect
					classNamePrefix="sesh-dropdown"
					value={selected_option}
					options={options}
					onChange={(selected: DurationDropdownElement) =>
						props.setValue(selected.value)
					}
					isValidNewOption={(inputValue: string) =>
						Boolean(
							tryParseCreatedSelection(inputValue, props.type)
						)
					}
					formatCreateLabel={(inputValue: string) => {
						const new_option_value = tryParseCreatedSelection(
							inputValue,
							props.type
						);

						if (new_option_value == null) {
							return;
						}

						return formatPluralDuration(
							new_option_value,
							props.type
						);
					}}
					onCreateOption={(inputValue: string) => {
						const new_option_value = tryParseCreatedSelection(
							inputValue,
							props.type
						);

						if (new_option_value == null) {
							return;
						}

						const new_option_dropdown = {
							label: formatPluralDuration(
								new_option_value,
								props.type
							),
							value: new_option_value,
						};

						setOptions((options) => {
							return [...options, new_option_dropdown].sort(
								(a, b) => a.value - b.value
							);
						});

						new_option_value *
							1000 *
							60 *
							(props.type === "hour" ? 60 : 1);

						props.setValue(new_option_value);
					}}
					isDisabled={false}
				/>
			</div>
		</div>
	);
}

function tryParseCreatedSelection(input: string, type: "hour" | "minute") {
	const new_option_value = input
		.split(" ")
		.map((val) => parseInt(val))
		.find((val) => !isNaN(val));

	if (new_option_value === undefined) {
		return;
	}

	if (new_option_value < 0) {
		return;
	}

	if (type === "minute" && new_option_value >= 60) {
		return;
	}

	return new_option_value;
}
